import type { MarkSpec, NodeSpec } from "prosemirror-model";
import { Schema } from "prosemirror-model";
import { tableNodes } from "prosemirror-tables";

import { IGNORE_SAME_ORIGIN_LINK_CLICK_ATTRIBUTE } from "@/utils/navigation-events";

const getCodeLanguage = (element: HTMLElement) => {
  const explicitLanguage =
    element.getAttribute("data-language") ||
    element.getAttribute("data-params") ||
    element.querySelector("code")?.getAttribute("data-language");

  if (explicitLanguage) {
    return explicitLanguage;
  }

  const codeClassName = element.querySelector("code")?.className || "";
  return /\blanguage-([\w-]+)/.exec(codeClassName)?.[1] || "";
};

const getTaskCheckedState = (element: HTMLElement) => {
  if (element.dataset.checked === "true") {
    return true;
  }

  if (element.dataset.checked === "false") {
    return false;
  }

  const checkbox = element.querySelector<HTMLInputElement>(
    'input[type="checkbox"]',
  );

  return checkbox ? checkbox.checked : null;
};

const nodes: Record<string, NodeSpec> = {
  doc: {
    content: "block+",
  },

  paragraph: {
    content: "inline*",
    group: "block",
    parseDOM: [{ tag: "p" }],
    toDOM: () => ["p", 0],
  },

  blockquote: {
    content: "block+",
    group: "block",
    parseDOM: [{ tag: "blockquote" }],
    toDOM: () => ["blockquote", 0],
  },

  horizontal_rule: {
    group: "block",
    parseDOM: [{ tag: "hr" }],
    toDOM: () => ["hr"],
  },

  heading: {
    attrs: {
      level: {
        default: 1,
      },
    },
    content: "inline*",
    defining: true,
    group: "block",
    parseDOM: [1, 2, 3, 4, 5, 6].map((level) => ({
      attrs: { level },
      tag: `h${level}`,
    })),
    toDOM: (node) => [`h${node.attrs.level}`, 0],
  },

  code_block: {
    attrs: {
      language: {
        default: "",
      },
    },
    code: true,
    content: "text*",
    defining: true,
    group: "block",
    marks: "",
    parseDOM: [
      {
        getAttrs: (node) => ({
          language: node instanceof HTMLElement ? getCodeLanguage(node) : "",
        }),
        preserveWhitespace: "full",
        tag: "pre",
      },
    ],
    toDOM: (node) => [
      "pre",
      node.attrs.language ? { "data-language": node.attrs.language } : {},
      ["code", 0],
    ],
  },

  ordered_list: {
    attrs: {
      order: {
        default: 1,
      },
      tight: {
        default: false,
      },
    },
    content: "list_item+",
    group: "block",
    parseDOM: [
      {
        getAttrs: (node) => ({
          order:
            node instanceof HTMLOListElement && node.hasAttribute("start")
              ? Number(node.getAttribute("start")) || 1
              : 1,
          tight:
            node instanceof HTMLElement &&
            node.getAttribute("data-tight") === "true",
        }),
        tag: "ol",
      },
    ],
    toDOM: (node) => [
      "ol",
      {
        "data-tight": node.attrs.tight ? "true" : null,
        start: node.attrs.order === 1 ? null : node.attrs.order,
      },
      0,
    ],
  },

  bullet_list: {
    attrs: {
      tight: {
        default: false,
      },
    },
    content: "list_item+",
    group: "block",
    parseDOM: [
      {
        getAttrs: (node) => ({
          tight:
            node instanceof HTMLElement &&
            node.getAttribute("data-tight") === "true",
        }),
        tag: "ul",
      },
    ],
    toDOM: (node) => [
      "ul",
      { "data-tight": node.attrs.tight ? "true" : null },
      0,
    ],
  },

  list_item: {
    attrs: {
      checked: {
        default: null,
      },
    },
    content: "block+",
    defining: true,
    parseDOM: [
      {
        getAttrs: (node) => ({
          checked:
            node instanceof HTMLElement ? getTaskCheckedState(node) : null,
        }),
        tag: "li",
      },
    ],
    toDOM: (node) => [
      "li",
      {
        "data-checked":
          typeof node.attrs.checked === "boolean"
            ? String(node.attrs.checked)
            : null,
      },
      0,
    ],
  },

  text: {
    group: "inline",
  },

  image: {
    attrs: {
      alt: {
        default: null,
      },
      src: {},
      title: {
        default: null,
      },
      uploadStatus: {
        default: null,
      },
    },
    draggable: true,
    group: "inline",
    inline: true,
    parseDOM: [
      {
        getAttrs: (node) =>
          node instanceof HTMLImageElement
            ? {
                alt: node.getAttribute("alt"),
                src: node.getAttribute("src"),
                title: node.getAttribute("title"),
                uploadStatus: null,
              }
            : false,
        tag: "img[src]",
      },
    ],
    toDOM: (node) => [
      "img",
      {
        alt: node.attrs.alt,
        src: node.attrs.src,
        title: node.attrs.title,
      },
    ],
  },

  mention: {
    attrs: {
      displayName: {
        default: null,
      },
      publicKey: {
        default: null,
      },
      trigger: {
        default: "@",
      },
      username: {},
    },
    group: "inline",
    inline: true,
    atom: true,
    selectable: false,
    parseDOM: [
      {
        getAttrs: (node) =>
          node instanceof HTMLElement
            ? {
                displayName: node.dataset.mentionDisplayName || null,
                publicKey: node.dataset.mentionPublicKey || null,
                trigger:
                  node.dataset.mentionTrigger ||
                  (node.textContent?.startsWith("$") ? "$" : "@"),
                username:
                  node.dataset.mentionUsername ||
                  node.textContent?.replace(/^[@$]/, "") ||
                  "",
              }
            : false,
        tag: "span[data-mention-username]",
      },
    ],
    toDOM: (node) => [
      "span",
      {
        "data-mention-display-name": node.attrs.displayName,
        "data-mention-public-key": node.attrs.publicKey,
        "data-mention-trigger": node.attrs.trigger,
        "data-mention-username": node.attrs.username,
        "data-type": "mention",
      },
      `${node.attrs.trigger || "@"}${node.attrs.username}`,
    ],
  },

  hard_break: {
    group: "inline",
    inline: true,
    parseDOM: [{ tag: "br" }],
    selectable: false,
    toDOM: () => ["br"],
  },

  footnote_reference: {
    attrs: {
      label: {},
    },
    group: "inline",
    inline: true,
    atom: true,
    selectable: false,
    parseDOM: [
      {
        getAttrs: (node) =>
          node instanceof HTMLElement
            ? { label: node.dataset.footnoteLabel || node.textContent || "" }
            : false,
        tag: "sup[data-footnote-label]",
      },
    ],
    toDOM: (node) => [
      "sup",
      { "data-footnote-label": node.attrs.label },
      `[^${node.attrs.label}]`,
    ],
  },

  footnote_definition: {
    attrs: {
      label: {},
    },
    content: "block+",
    defining: true,
    group: "block",
    parseDOM: [
      {
        getAttrs: (node) =>
          node instanceof HTMLElement
            ? { label: node.dataset.footnoteLabel || "" }
            : false,
        tag: "section[data-footnote-label]",
      },
    ],
    toDOM: (node) => [
      "section",
      { "data-footnote-label": node.attrs.label },
      0,
    ],
  },

  ...tableNodes({
    cellAttributes: {},
    cellContent: "inline*",
    tableGroup: "block",
  }),
};

const marks: Record<string, MarkSpec> = {
  em: {
    parseDOM: [
      { tag: "i" },
      { tag: "em" },
      { style: "font-style=italic" },
      {
        clearMark: (mark) => mark.type.name === "em",
        style: "font-style=normal",
      },
    ],
    toDOM: () => ["em", 0],
  },

  strong: {
    parseDOM: [
      { tag: "strong" },
      {
        getAttrs: (node) =>
          node instanceof HTMLElement
            ? node.style.fontWeight !== "normal" && null
            : null,
        tag: "b",
      },
      {
        clearMark: (mark) => mark.type.name === "strong",
        style: "font-weight=400",
      },
      {
        getAttrs: (value) =>
          typeof value === "string" &&
          /^(bold(er)?|[5-9]\d{2,})$/.test(value) &&
          null,
        style: "font-weight",
      },
    ],
    toDOM: () => ["strong", 0],
  },

  strike: {
    parseDOM: [
      { tag: "s" },
      { tag: "del" },
      { style: "text-decoration=line-through" },
    ],
    toDOM: () => ["s", 0],
  },

  link: {
    attrs: {
      href: {},
      title: {
        default: null,
      },
    },
    inclusive: false,
    parseDOM: [
      {
        getAttrs: (node) =>
          node instanceof HTMLAnchorElement
            ? {
                href: node.getAttribute("href"),
                title: node.getAttribute("title"),
              }
            : false,
        tag: "a[href]",
      },
    ],
    toDOM: (node) => [
      "a",
      {
        ...node.attrs,
        [IGNORE_SAME_ORIGIN_LINK_CLICK_ATTRIBUTE]: "true",
      },
      0,
    ],
  },

  code: {
    code: true,
    parseDOM: [{ tag: "code" }],
    toDOM: () => ["code", 0],
  },
};

export const focusSchema = new Schema({
  marks,
  nodes,
});

export type FocusSchema = typeof focusSchema;
